import { keepPreviousData, useQueries, UseQueryResult } from "@tanstack/react-query";
import groupBy from "lodash/groupBy";
import isEmpty from "lodash/isEmpty";
import mapValues from "lodash/mapValues";
import some from "lodash/some";
import zipWith from "lodash/zipWith";
import { useCalendarGridScope } from "PFApp/booking/parts/hooks";
import { usePastDatesStateContext } from "PFApp/booking/parts/overview/booking_module_calendar/booking_calendar/past_dates_context";
import { ChildrenType, getRequestsData } from "PFApp/booking/parts/overview/side_panel_row/get_request_data";
import {
  useBookingModuleStore,
  useCalendarRoot,
  useWorkforceProfilesDataIfAvailable
} from "PFApp/booking/store";
import { CalendarRoot } from "PFApp/booking/types/calendar_root";
import { RowOriginal } from "PFApp/booking/types/row";
import { useFiltersV2Select } from "PFApp/components/filters_v2";
import api from "PFCore/api";
import { JsonValue, stringify } from "PFCore/helpers/use_deterministic_stringify";
import { useErrorsGrowl } from "PFCore/hooks";
import useBookingCategories from "PFCore/hooks/use_booking_categories";
import { PaginatedCollection } from "PFTypes";
import { useEffect, useMemo } from "react";

import { rowChildrenKeys } from "./query_keys";

export type RowChildrenResponse = PaginatedCollection<RowOriginal[]>;

export type RowsChildrenResult = {
  rowKey: string;
  childrenType: string;
  response: RowChildrenResponse;
};
export type ChildrenDataByRowKey = Record<string, Record<ChildrenType, RowsChildrenResult | undefined>>;

type UseRowsChildrenReturn = {
  childrenFetchingByRowKey: Record<string, boolean>;
  childrenDataByRowKey: ChildrenDataByRowKey;
};

export const useRowsChildren = (): UseRowsChildrenReturn => {
  const growlError = useErrorsGrowl();
  const { childrenFilters } = useFiltersV2Select("childrenFilters");
  const { bookingCategories } = useBookingCategories();
  const { pastFetchesCount } = usePastDatesStateContext();
  const dateRange = useCalendarGridScope({ pastFetchesCount });
  const calendarRoot = useCalendarRoot();
  const rows = useBookingModuleStore((state) => state[calendarRoot].rows);
  const expandedRowsState = useBookingModuleStore((state) => state[calendarRoot].expandedRowsState);
  const paginationByRowKey = useBookingModuleStore((state) => state[calendarRoot].paginationByRowKey);
  const isRoleLevelHiddenForEngagementsRoot = useBookingModuleStore(
    (state) => state[CalendarRoot.ProjectsEngagements].isRoleLevelHidden
  );
  const { bookingsVisibility } = useWorkforceProfilesDataIfAvailable("bookingsVisibility");

  const rowRequests = useMemo(() => {
    const expandedRows = rows.filter(({ key }) => !!expandedRowsState[key]);
    return expandedRows.flatMap((row) => {
      const requests = getRequestsData({
        calendarRoot,
        row,
        dateRange,
        childrenFilters,
        bookingCategories,
        isRoleLevelHiddenForEngagementsRoot,
        bookingsVisibility
      });
      return requests.map((request) => ({ rowKey: row.key, request }));
    });
  }, [
    stringify({
      bookingCategories,
      childrenFilters,
      dateRange,
      expandedRowsState,
      rows
    } as unknown as JsonValue),
    calendarRoot,
    isRoleLevelHiddenForEngagementsRoot,
    bookingsVisibility
  ]);

  const queriesConfig = rowRequests.flatMap(({ rowKey, request }) => {
    const dataKey = request.options?.method === "POST" ? "data" : "params";
    const currentPage = paginationByRowKey[rowKey]?.page ?? 1;
    const listTotalPages = paginationByRowKey[rowKey]?.metaByChildrenType?.[request.childrenType]?.totalPages;
    const effectivePage = listTotalPages ? Math.min(currentPage, listTotalPages) : currentPage;
    return {
      queryKey: rowChildrenKeys.ofRow(
        calendarRoot,
        rowKey,
        request.childrenType,
        request[dataKey]?.dateRange,
        request[dataKey]?.filters ?? undefined,
        request[dataKey]?.perPage ?? null,
        effectivePage
      ),
      queryFn: async (): Promise<RowsChildrenResult> => {
        const response = await api({
          url: request.url,
          [dataKey]: { ...request[dataKey], page: effectivePage },
          ...request.options
        });
        return {
          rowKey,
          childrenType: request.childrenType,
          response: request.mapResponse ? await request.mapResponse(response) : response
        };
      },
      placeholderData: keepPreviousData,
      retry: 1
    };
  });

  const queries: UseQueryResult<RowsChildrenResult>[] = useQueries({
    queries: queriesConfig
  });

  const queriesDataStringified = JSON.stringify(queries.map(({ data }) => data));
  const queriesErrorsStringified = JSON.stringify(queries.map(({ error }) => error));
  const queriesIsErrorStringified = JSON.stringify(queries.map(({ isError }) => isError));

  useEffect(() => {
    if (isEmpty(queries)) {
      return;
    }

    rowRequests.forEach((rowRequest, index) => {
      const query = queries[index];
      if (query.isError && query.error) {
        growlError(query.error);
      }
    });
  }, [queriesDataStringified, queriesErrorsStringified, queriesIsErrorStringified]); //eslint-disable-line react-hooks/exhaustive-deps

  const { childrenFetchingByRowKey, childrenDataByRowKey } = useMemo(() => {
    const requestsWithQuery = zipWith(rowRequests, queries, ({ rowKey, request }, query) => ({
      rowKey,
      request,
      query
    }));

    const childrenFetchingByRowKey = mapValues(groupBy(requestsWithQuery, "rowKey"), (requests) =>
      some(requests, ({ query }) => query.isFetching)
    );
    const childrenDataByRowKey: ChildrenDataByRowKey = mapValues(
      groupBy(requestsWithQuery, "rowKey"),
      (requests) =>
        mapValues(
          groupBy(requests, ({ request }) => request.childrenType),
          (requests) => requests[0]?.query.data
        ) as ChildrenDataByRowKey[string]
    );
    return {
      childrenFetchingByRowKey,
      childrenDataByRowKey
    };
  }, [rowRequests, stringify({ queries } as unknown as JsonValue)]);

  return {
    childrenFetchingByRowKey,
    childrenDataByRowKey
  };
};
